import { finalize, map, shareReplay, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import {
  CommonChemicalElement,
  CommonCommodity,
  CommonCommodityPrecocity,
  CommonCountry,
  CommonGroup,
  CommonIntendedUse,
  CommonIntendedUseCommodityMapping,
  CommonMeasureUnit,
  CommonPhenoStage,
  CommonPrecocity,
  CommonQuality,
  CommonRegion,
  CommonSoilType,
  CommonStrategy,
  CommonStrategyLink,
  CommonUse,
  CommonVariety,
} from '../_models/commonService';

@Injectable()
export class CommonService {
  private apiBaseUrl = environment.commonServiceUrl;
  $elements: BehaviorSubject<CommonChemicalElement[]> = new BehaviorSubject<
    CommonChemicalElement[]
  >(null);
  private elementsFetchInProgress$: Observable<CommonChemicalElement[]> | null = null;
  $measureUnits: BehaviorSubject<CommonMeasureUnit[]> = new BehaviorSubject<CommonMeasureUnit[]>(
    []
  );
  $regions: BehaviorSubject<CommonRegion[]> = new BehaviorSubject<CommonRegion[]>([]);
  $italianRegions: BehaviorSubject<CommonRegion[]> = new BehaviorSubject<CommonRegion[]>(null);
  $countries: BehaviorSubject<CommonCountry[]> = new BehaviorSubject<CommonCountry[]>([]);
  $groups: BehaviorSubject<CommonGroup[]> = new BehaviorSubject<CommonGroup[]>(null);
  $soilTypes: BehaviorSubject<CommonSoilType[]> = new BehaviorSubject<CommonSoilType[]>([]);
  $intendedUses: BehaviorSubject<CommonIntendedUse[]> = new BehaviorSubject<CommonIntendedUse[]>(
    []
  );
  $qualities: BehaviorSubject<CommonQuality[]> = new BehaviorSubject<CommonQuality[]>([]);
  $uses: BehaviorSubject<CommonUse[]> = new BehaviorSubject<CommonUse[]>([]);
  $commodities: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  $cropAttributes: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  $intendedUsesCommodityMapping: BehaviorSubject<CommonIntendedUseCommodityMapping[]> =
    new BehaviorSubject<CommonIntendedUseCommodityMapping[]>([]);
  $phenoStages: BehaviorSubject<CommonPhenoStage[]> = new BehaviorSubject<CommonPhenoStage[]>([]);
  $strategies: BehaviorSubject<CommonStrategy[]> = new BehaviorSubject<CommonStrategy[]>([]);

  constructor(private http: HttpClient) {}

  /**
   * @description: get all common chemical elements from periodic table
   * @returns {Observable<CommonChemicalElement[]>}
   * */
  getAndStoreCommonElements(): Observable<CommonChemicalElement[]> {
    if (this.elementsFetchInProgress$) {
      // Return ongoing request observable if a fetch is already in progress
      return this.elementsFetchInProgress$;
    }

    this.elementsFetchInProgress$ = this.http
      .get<CommonChemicalElement[]>(this.apiBaseUrl + 'elements')
      .pipe(
        tap((response) => {
          this.$elements.next(response);
        }),
        finalize(() => {
          this.elementsFetchInProgress$ = null;
        }),
        shareReplay(1)
      );

    return this.elementsFetchInProgress$;
  }

  /**
   * @description: Get elements by group name
   * @param groupName{string}
   * @returns {Observable<CommonChemicalElement[]>}
   */
  getCommonElementsByGroupName(groupName: string): Observable<CommonChemicalElement[]> {
    return this.http
      .get<CommonChemicalElement[]>(this.apiBaseUrl + 'elements', {
        params: {
          groupName,
        },
      })
      .pipe(
        map((response) => {
          return response;
        })
      );
  }

  /**
   * @description: get all italian regions
   * @returns {Observable<CommonRegion[]>}
   */
  getItalianRegions(): Observable<CommonRegion[]> {
    return this.http
      .get<CommonRegion[]>(this.apiBaseUrl + 'regions?groupName=ITALIAN_REGIONS')
      .pipe(
        map((response) => {
          this.$italianRegions.next(response);
          return response;
        })
      );
  }

  /**
   * @description: get common chemical element by periodic table name, upper and lower case are valid
   * @param chemicalName
   * @returns {CommonChemicalElement}
   */
  getCommonElementByPeriodicTableName(chemicalName: string): CommonChemicalElement {
    const elements = this.$elements.getValue();
    return elements.find((element) => {
      if (element.periodicTableName) {
        return element.periodicTableName.toLowerCase() === chemicalName.toLowerCase();
      } else {
        return null;
      }
    });
  }

  /**
   * @description: get common chemical element by its name, upper and lower case are valid
   * @param chemicalName
   * @returns {CommonChemicalElement}
   */
  getCommonElementByName(chemicalName: string): CommonChemicalElement {
    const elements = this.$elements.getValue();
    return elements.find((element) => {
      if (element.name) {
        return element.name.toLowerCase() === chemicalName.toLowerCase();
      } else {
        return null;
      }
    });
  }

  /**
   *  @description: returns available soil types
   * @param groupId {string} optional
   * @returns {Observable<CommonSoilType[]>}
   * */
  getSoilTypes(groupId?: string): Observable<CommonSoilType[]> {
    return this.http
      .get<CommonSoilType[]>(this.apiBaseUrl + `soilTypes${groupId ? '&groupId=' + groupId : ''}`)
      .pipe(
        map((response) => {
          this.$soilTypes.next(response);
          return response;
        })
      );
  }

  /**
   * @description: returns available groups
   * @returns {Observable<CommonGroup[]>}
   */
  getAndStoreGroups(): Observable<CommonGroup[]> {
    return this.http.get<CommonGroup[]>(this.apiBaseUrl + 'groups').pipe(
      map((response) => {
        this.$groups.next(response);
        return response;
      })
    );
  }

  /**
   * @description: returns a group given the group name
   * @param groupName
   */
  getGroupByGroupName(groupName: string): CommonGroup {
    const groups = this.$groups.getValue();
    return groups.find((group) => group.name === groupName);
  }

  /**
   * @description: returns available measure units
   *  @param groupId {string} optional
   * @returns {Observable<CommonMeasureUnit[]>}
   */
  getAndStoreMeasureUnits(groupId?: string): Observable<CommonMeasureUnit[]> {
    return this.http
      .get<
        CommonMeasureUnit[]
      >(this.apiBaseUrl + `unitsOfMeasure${groupId ? '&groupId=' + groupId : ''}`)
      .pipe(
        map((response) => {
          this.$measureUnits.next(response);
          return response;
        })
      );
  }

  getMeasureUnitById(id: string): CommonMeasureUnit {
    const measureUnits = this.$measureUnits.getValue();
    return measureUnits.find((unit) => unit.id === id);
  }

  /**
   * @description: returns available regions, regions can be geographic regions, administrative or disciplinary, like DOC DOCG ecc...
   * @param groupId {string} optional
   * @returns {Observable<CommonRegion[]>}
   */
  getRegions(groupId?: string): Observable<CommonRegion[]> {
    return this.http
      .get<CommonRegion[]>(this.apiBaseUrl + `regions${groupId ? '&groupId=' + groupId : ''}`)
      .pipe(
        map((response) => {
          this.$regions.next(response);
          return response;
        })
      );
  }

  /**
   * @description: returns all world countries, by passing a group id
   * you can filter countries by continent, region or other arbitrary groups
   * @param groupId {string} optional
   * @returns {Observable<CommonCountry[]>}
   */
  getCountries(groupId?: string): Observable<CommonCountry[]> {
    return this.http
      .get<CommonCountry[]>(this.apiBaseUrl + `countries${groupId ? '&groupId=' + groupId : ''}`)
      .pipe(
        map((response) => {
          this.$countries.next(response);
          return response;
        })
      );
  }

  /**
   * @description: returns all the possible intended uses for the crops
   * @param groupId {string} optional
   * @returns {Observable<CommonIntendedUse[]>}
   */
  getIntendedUses(
    groupId?: string,
    name?: string,
    externalId?: any,
    externalSource?: any
  ): Observable<CommonIntendedUse[]> {
    return this.http
      .get<CommonIntendedUse[]>(this.apiBaseUrl + 'intendedUse', {
        params: {
          ...(groupId ? { groupId } : {}),
          ...(name ? { name } : {}),
          ...(externalId ? { externalId } : {}),
          ...(externalSource ? { externalSource } : {}),
        },
      })
      .pipe(
        map((response) => {
          this.$intendedUses.next(response);
          return response;
        })
      );
  }

  /**
   * @description: returns all the possible uses for the crops
   * @returns {Observable<any[]>}
   */
  getCropAttributes(): Observable<any[]> {
    return this.http
      .get<CommonUse[]>(this.apiBaseUrl + 'cropAttributes', {
        headers: {
          'x-api-key': '5323d47b-daac-4863-81db-13c93e01f90f',
          Accept: 'application/json',
        },
      })
      .pipe(
        map((response) => {
          this.$cropAttributes.next(response);
          return response;
        })
      );
  }

  /**
   * @description: returns the mapping between commodities and intended uses
   * @param groupId {string} optional
   * @returns {Observable<CommonIntendedUseCommodityMapping[]>}
   */
  getIntendedUsesCommodityMapping(
    groupId?: string
  ): Observable<CommonIntendedUseCommodityMapping[]> {
    return this.http
      .get<CommonIntendedUseCommodityMapping[]>(this.apiBaseUrl + 'commodityIntendedUse', {
        params: {
          ...(groupId ? { groupId } : {}),
        },
      })
      .pipe(
        map((response) => {
          this.$intendedUsesCommodityMapping.next(response);
          return response;
        })
      );
  }

  /**
   * @description: returns all the possible phenologic stages for the crops
   * @param groupId {string} optional
   * @param name {string} optional
   * @param externalId {string} optional
   * @param externalSource {string} optional
   * @returns {Observable<CommonPhenoStage[]>}
   */
  getPhenologicStages(
    groupId?: string,
    name?: any,
    externalId?: any,
    externalSource?: any
  ): Observable<CommonPhenoStage[]> {
    return this.http
      .get<CommonPhenoStage[]>(this.apiBaseUrl + 'phenologicalPhase', {
        params: {
          ...(groupId ? { groupId } : {}),
          ...(name ? { name } : {}),
          ...(externalId ? { externalId } : {}),
          ...(externalSource ? { externalSource } : {}),
        },
      })
      .pipe(
        map((response) => {
          this.$phenoStages.next(response);
          return response;
        })
      );
  }

  /**
   * @description: returns all the possible strategy
   * @returns {Observable<CommonStrategy[]>}
   */
  getIrrigationStrategy(): Observable<CommonStrategy[]> {
    return this.http.get<CommonStrategy[]>(this.apiBaseUrl + 'irrigationStrategy').pipe(
      map((response) => {
        this.$strategies.next(response);
        return response;
      })
    );
  }

  /**
   * @description: returns all the possible strategy links
   * @returns {Observable<CommonStrategyLink[]>}
   */
  getIrrigationStrategyLink(): Observable<CommonStrategyLink[]> {
    return this.http.get<CommonStrategyLink[]>(this.apiBaseUrl + 'irrigationStrategyLink').pipe(
      map((response) => {
        this.$strategies.next(response);
        return response;
      })
    );
  }

  /**
   * @description: returns all the possible phenologic stages for the crops including startDate and endDate
   * @param commodityId {string} optional
   * @returns {Observable<CommonPhenoStagePerCommodity[]>}
   */
  getCommodityPhenologicalPhase(commodityId?) {
    return this.http
      .get<any>(this.apiBaseUrl + 'commodityPhenologicalPhase', {
        params: {
          ...(commodityId ? { commodityId } : {}),
        },
      })
      .pipe(
        map((response) => {
          return response;
        })
      );
  }

  /**
   * @description: returns all the commodities
   * @param groupId {string} optional
   * @param name {string} optional
   * @param externalId {string} optional
   * @param externalSource {string} optional
   * @param eppo {string} optional
   */
  getCommodities(
    groupId?: string,
    name?: any,
    externalId?: any,
    externalSource?: any,
    eppo?: string
  ): Observable<CommonCommodity[]> {
    return this.http.get<CommonCommodity[]>(this.apiBaseUrl + 'commodities', {
      params: {
        ...(groupId ? { groupId } : {}),
        ...(name ? { name } : {}),
        ...(externalId ? { externalId } : {}),
        ...(externalSource ? { externalSource } : {}),
        ...(eppo ? { eppo } : {}),
      },
    });
  }

  /**
   * @description: returns all precocities
   */
  getPrecocities(params?: {
    groupId?: string;
    name?: string;
    externalId?: string;
    externalSource?: string;
    translationKey?: string;
    size?: number;
    page?: number;
  }): Observable<CommonPrecocity[]> {
    return this.http.get<CommonPrecocity[]>(this.apiBaseUrl + 'precocities', {
      params: {
        ...params,
      },
    });
  }

  /**
   * @description: returns all commodity precocities
   */
  getCommodityPrecocity(params?: {
    name?: string;
    commodityId?: string;
    precocityId?: string;
    size?: number;
    page?: number;
    count?: boolean;
  }): Observable<CommonCommodityPrecocity[]> {
    return this.http.get<CommonCommodityPrecocity[]>(this.apiBaseUrl + 'commodityPrecocity', {
      params: {
        ...params,
      },
    });
  }

  /**
   * @description: returns all the common varieties
   * @param groupId {string} optional
   * @param name {string} optional
   * @param externalId {string} optional
   * @param externalSource {string} optional
   * @param eppo {string} optional
   */
  getVarieties(
    groupId?: string,
    name?: any,
    externalId?: any,
    externalSource?: any,
    eppo?: string
  ): Observable<CommonVariety[]> {
    return this.http.get<CommonVariety[]>(this.apiBaseUrl + 'commodities', {
      params: {
        ...(groupId ? { groupId } : {}),
        ...(name ? { name } : {}),
        ...(externalId ? { externalId } : {}),
        ...(externalSource ? { externalSource } : {}),
        ...(eppo ? { eppo } : {}),
      },
    });
  }

  /**
   * @description: Returns all the commodity varieties
   * @param groupId {string} optional
   * @param name {string} optional
   * @param externalId {string} optional
   * @param externalSource {string} optional
   * @param eppo {string} optional
   * @returns {Observable<CommonQuality[]>}
   */
  getQualities(
    groupId?: string,
    name?: any,
    externalId?: any,
    externalSource?: any,
    eppo?: string
  ): Observable<CommonQuality[]> {
    return this.http.get<CommonQuality[]>(this.apiBaseUrl + 'qualities', {
      params: {
        ...(groupId ? { groupId } : {}),
        ...(name ? { name } : {}),
        ...(externalId ? { externalId } : {}),
        ...(externalSource ? { externalSource } : {}),
        ...(eppo ? { eppo } : {}),
      },
    });
  }

  /**
   * @description: returns all the possible uses for hbfarm commodities
   * @param groupId {string} optional
   * @param name {string} optional
   * @param externalId {string} optional
   * @param externalSource {string} optional
   * @param eppo {string} optional
   * @returns {Observable<CommonUse[]>}
   */
  getUses(
    groupId?: string,
    name?: any,
    externalId?: any,
    externalSource?: any,
    eppo?: string
  ): Observable<CommonUse[]> {
    return this.http.get<CommonUse[]>(this.apiBaseUrl + 'uses', {
      params: {
        ...(groupId ? { groupId } : {}),
        ...(name ? { name } : {}),
        ...(externalId ? { externalId } : {}),
        ...(externalSource ? { externalSource } : {}),
        ...(eppo ? { eppo } : {}),
      },
    });
  }
}
